import { Menu, Transition } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import { createContext, Fragment, ReactNode, useContext } from 'react';
import { getChildrenByType } from '../../utils';
import Button from './Button';
import Spinner from '../spinner/Spinner';

const buttonStyles = {
    base: 'focus:outline-none focus:ring-2 disabled:cursor-default disabled:opacity-50 focus:ring-indigo-500 focus:z-10',
    variant: {
        primary:
            'border-transparent font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:hover:bg-indigo-600 border shadow-sm',
        secondary:
            'border-gray-300 font-medium dark:text-white/[0.87] text-gray-700 bg-white dark:bg-white/[0.12] dark:hover:bg-white/[0.09] hover:bg-gray-50 dark:disabled:hover:bg-white/[0.12] disabled:hover:bg-white border dark:border-white/[0.12] shadow-sm',
    },
    size: {
        xs: 'px-2.5 py-1.5 text-xs',
        sm: 'px-3 py-2 text-sm leading-4',
        md: 'px-4 py-2 text-sm',
        lg: 'px-4 py-2 text-base',
        xl: 'px-6 py-3 text-base',
    },

    rounding: {
        xs: 'rounded-l',
        sm: 'rounded-l-md',
        md: 'rounded-l-md',
        lg: 'rounded-l-md',
        xl: 'rounded-l-md',
    },
};

const dropdownStyles = {
    variant: {
        primary:
            'border border-transparent font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:hover:bg-indigo-600 shadow-sm border-l-white',
        secondary: 'border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 focus:z-10',
    },
    padding: {
        xs: 'px-1 py-1.5',
        sm: 'px-2 py-2',
        md: 'px-2 py-2',
        lg: 'px-3 py-2',
        xl: 'px-4 py-3',
    },

    size: {
        xs: 'h-3 w-3',
        sm: 'h-4 w-4',
        md: 'h-5 w-5',
        lg: 'h-6 w-6',
        xl: 'h-6 w-6',
    },
    rounding: {
        xs: 'rounded-r',
        sm: 'rounded-r-md',
        md: 'rounded-r-md',
        lg: 'rounded-r-md',
        xl: 'rounded-r-md',
    },
};

export type ButtonDropdownProps = {
    variant: 'primary' | 'secondary';
    size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
    children?: ReactNode | ReactNode[];
    disabled?: boolean;
    isLoading?: boolean;
    label: string;
    onClick: () => void;
};

const ButtonContext = createContext<Pick<ButtonDropdownProps, 'variant' | 'size'>>({ variant: 'primary', size: 'md' });

function ButtonDropdown({ variant, size = 'md', disabled = false, children, label, onClick, isLoading }: ButtonDropdownProps): JSX.Element {
    const _children = getChildrenByType(children, SecondaryAction);

    if (_children.length === 0) {
        return <Button type={variant} size={size} disabled={disabled} isLoading={isLoading} label={label} onClick={onClick} />;
    }

    return (
        <div className="relative inline-flex">
            <button
                type="button"
                disabled={disabled}
                onClick={onClick}
                className={`flex items-center gap-4 ${buttonStyles.base} ${buttonStyles.variant[variant]} ${buttonStyles.size[size]} ${buttonStyles.rounding[size]}`}
            >
                {isLoading && <Spinner size="sm" />}
                {label}
            </button>
            <ButtonContext.Provider value={{ variant, size }}>
                <Menu as="div" className="-ml-px relative block">
                    <Menu.Button
                        disabled={disabled}
                        className={`relative h-full inline-flex items-center ${dropdownStyles.variant[variant]} ${dropdownStyles.padding[size]} ${dropdownStyles.rounding[size]} ${buttonStyles.base}`}
                    >
                        <span className="sr-only">Open options</span>
                        <ChevronDownIcon className={dropdownStyles.size[size]} aria-hidden="true" />
                    </Menu.Button>
                    <Transition
                        as={Fragment}
                        enter="transition ease-out duration-100"
                        enterFrom="transform opacity-0 scale-95"
                        enterTo="transform opacity-100 scale-100"
                        leave="transition ease-in duration-75"
                        leaveFrom="transform opacity-100 scale-100"
                        leaveTo="transform opacity-0 scale-95"
                    >
                        <Menu.Items className="z-50 origin-top-right absolute right-0 mt-2 -mr-1 w-max rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none ">
                            <div className="py-1">{_children}</div>
                        </Menu.Items>
                    </Transition>
                </Menu>
            </ButtonContext.Provider>
        </div>
    );
}

type SecondaryActionProps = {
    label: string;
    onClick: () => void;
    disabled?: boolean;
};

function SecondaryAction({ label, onClick, disabled = false }: SecondaryActionProps): JSX.Element {
    const context = useContext(ButtonContext);

    return (
        <Menu.Item>
            {({ active }): JSX.Element => {
                const primaryStyle = active ? 'bg-indigo-600 text-white' : 'text-gray-700';
                const secondaryStyle = active ? 'bg-gray-100 text-gray-900' : 'text-gray-700';

                return (
                    <button
                        disabled={disabled}
                        onClick={onClick}
                        className={`${buttonStyles.base} block w-full text-left  ${buttonStyles.size[context.size]} ${
                            context.variant === 'primary' ? primaryStyle : secondaryStyle
                        }`}
                    >
                        {label}
                    </button>
                );
            }}
        </Menu.Item>
    );
}

ButtonDropdown.SecondaryAction = SecondaryAction;
export default ButtonDropdown;
